import React, { useEffect, useState } from "react";
import "./SalesLetterPage.css";
import CollapsibleSection from "./CollapsibleSection";

function ManualImage({ src, alt, className = "", setLightboxSrc }) {
  return (
    <img
      src={src}
      alt={alt}
      className={`sl-image ${className}`}
      role="button"
      tabIndex={0}
      loading="lazy"
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          setLightboxSrc(src);
        }
      }}
      aria-label={`${alt}（クリックで拡大）`}
    />
  );
}

export default function SalesLetterPage() {
  const [, setLightboxSrc] = useState(null);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && setLightboxSrc(null);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  useEffect(() => {
    const els = document.querySelectorAll(".reveal");
    if (!els.length) return;
    const io = new IntersectionObserver(
      (entries) =>
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("reveal--in");
            io.unobserve(entry.target);
          }
        }),
      { threshold: 0.12 }
    );
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);

  useEffect(() => {
    const bar = document.querySelector(".sl-stickybar");
    const onScroll = () => {
      if (!bar) return;
      bar.classList.toggle("is-visible", window.scrollY > 480);
    };
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const CTA = (
    <a
      className="sl-btn sl-btn--primary"
      href="/subscribe/trial"
      target="_blank"
      rel="noopener noreferrer"
      data-cta="free-trial"
      aria-label="無料トライアルを開始"
    >
      無料で試す
    </a>
  );

  return (
    <>
      <main className="sl-page" role="main">
        <div className="sl-stickybar" aria-hidden>
          <div className="sl-container sl-stickybar__inner">
            <span className="sl-stickybar__text">
              広告費の無駄を減らし、売れる時間帯に予算を残す
            </span>
            {CTA}
          </div>
        </div>

        {/* Hero */}
        <header className="sl-hero">
          <div className="sl-container sl-hero__grid">
            <div className="sl-hero__copy">
              <h1 className="sl-hero__title reveal">
                朝には広告予算がなくなっていませんか？
              </h1>
              <ul className="sl-list reveal" style={{ marginBottom: 16 }}>
                <li>深夜も広告費が消化されている</li>
                <li>毎日キャンペーンを確認している</li>
                <li>売れる時間帯に予算が残っていない</li>
                <li>旅行中も広告が気になる</li>
              </ul>
              <p className="reveal">
                AdScheはAmazon広告のON/OFFと予算変更を自動化し、
                <strong>広告費の無駄を減らす</strong>ためのツールです。
              </p>
              <div className="sl-hero__cta reveal">{CTA}</div>
            </div>
            <div className="sl-hero__visual reveal">
              <ManualImage
                src="images/preparation.jpg"
                alt="広告管理ダッシュボード"
                className="sl-image--shadow"
                setLightboxSrc={setLightboxSrc}
              />
            </div>
          </div>
          <div className="sl-container sl-resultbar reveal">
            <div className="sl-resultbar__item">
              <span className="sl-resultbar__value">46%</span>
              <span className="sl-resultbar__label">広告費削減（導入事例）</span>
            </div>
            <div className="sl-resultbar__item">
              <span className="sl-resultbar__value">148,000円</span>
              <span className="sl-resultbar__label">毎月の削減額（事例）</span>
            </div>
            <div className="sl-resultbar__item">
              <span className="sl-resultbar__value">0円</span>
              <span className="sl-resultbar__label">30日間のお試し費用</span>
            </div>
          </div>
        </header>

        {/* Case Studies */}
        <section className="sl-section sl-section--alt" aria-labelledby="cases-title">
          <div className="sl-container">
            <h2 id="cases-title" className="sl-heading reveal">
              実際の改善事例
            </h2>
            <div className="sl-case-grid">
              <article className="sl-case-card reveal">
                <h3 className="sl-case-card__title">
                  売上は維持しながら広告費46%削減
                </h3>
                <div className="sl-case-card__compare">
                  <div className="sl-case-card__phase">
                    <p className="sl-case-card__phase-label">導入前</p>
                    <p className="sl-case-card__metric">
                      売上<strong>2,750,000円</strong>
                    </p>
                    <p className="sl-case-card__metric">
                      広告費<strong>320,000円</strong>
                    </p>
                  </div>
                  <div className="sl-case-card__phase">
                    <p className="sl-case-card__phase-label">導入後</p>
                    <p className="sl-case-card__metric">
                      売上<strong>2,800,000円</strong>
                    </p>
                    <p className="sl-case-card__metric">
                      広告費<strong>172,000円</strong>
                    </p>
                  </div>
                </div>
                <ul className="sl-case-card__results">
                  <li>✅ 広告費46%削減</li>
                  <li>✅ 毎月148,000円削減</li>
                  <li>✅ 年間1,776,000円削減</li>
                </ul>
              </article>
              <article className="sl-case-card reveal">
                <h3 className="sl-case-card__title">
                  売上を落とさず広告費を圧縮
                </h3>
                <div className="sl-case-card__compare">
                  <div className="sl-case-card__phase">
                    <p className="sl-case-card__phase-label">導入前</p>
                    <p className="sl-case-card__metric">
                      売上<strong>1,500,000円</strong>
                    </p>
                    <p className="sl-case-card__metric">
                      広告費<strong>180,000円</strong>
                    </p>
                  </div>
                  <div className="sl-case-card__phase">
                    <p className="sl-case-card__phase-label">導入後</p>
                    <p className="sl-case-card__metric">
                      売上<strong>1,500,000円</strong>
                    </p>
                    <p className="sl-case-card__metric">
                      広告費<strong>150,000円</strong>
                    </p>
                  </div>
                </div>
                <ul className="sl-case-card__results">
                  <li>✅ 広告費30,000円削減</li>
                  <li>✅ 年間360,000円削減</li>
                </ul>
              </article>
            </div>
            <p className="reveal" style={{ marginTop: 16, fontSize: ".9rem", color: "var(--sl-sub)" }}>
              ※商品ジャンルや運用状況により結果は異なります。
            </p>
          </div>
        </section>

        {/* Pain Points */}
        <section className="sl-section" aria-labelledby="pain-title">
          <div className="sl-container">
            <h2 id="pain-title" className="sl-heading reveal">
              こんなお悩みありませんか？
            </h2>
            <div className="sl-pain-grid">
              <div className="sl-pain-card reveal">朝起きると予算切れ</div>
              <div className="sl-pain-card reveal">深夜も広告が動いている</div>
              <div className="sl-pain-card reveal">毎日管理画面を確認している</div>
              <div className="sl-pain-card reveal">旅行中も広告が気になる</div>
              <div className="sl-pain-card reveal">ACOSが改善しない</div>
              <div className="sl-pain-card reveal">売れる時間帯に予算が残らない</div>
            </div>
          </div>
        </section>

        {/* Solution */}
        <section className="sl-section sl-section--muted" aria-labelledby="solution-title">
          <div className="sl-container">
            <h2 id="solution-title" className="sl-heading reveal">
              AdScheが解決します
            </h2>
            <p className="sl-lead reveal">
              AdScheは広告を賢くするツールではありません。
              <br />
              <strong>広告費の無駄を減らすツール</strong>です。
            </p>
            <p className="reveal">
              難しいAIもありません。複雑な最適化もありません。
            </p>
            <p className="reveal">
              売れない時間帯を止める。売れる時間帯に予算を残す。それだけです。
            </p>
            <p className="reveal">
              しかしそれだけで十分なケースがあります。
            </p>
          </div>
        </section>

        {/* Story (compressed) */}
        <section className="sl-section sl-section--alt">
          <div className="sl-container">
            <h2 className="sl-heading reveal">
              なぜAdScheを作ったのか
            </h2>
            <p className="reveal">
              毎朝キャンペーンマネージャーを開くと、予算は大幅に消化されているのに売上は伸びない。
              その度に「広告費の無駄遣いをしている」と感じていました。
            </p>
          </div>
          <div className="sl-container sl-zigzag" style={{ marginTop: 12 }}>
            <div className="sl-zigzag__media reveal">
              <ManualImage
                src="/images/tool.jpg"
                alt="高額ツール"
                className="sl-image--card"
                setLightboxSrc={setLightboxSrc}
              />
            </div>
            <div className="sl-zigzag__copy">
              <h3 className="sl-subheading reveal">
                高額ツールを使っても、欲しかったのは時間帯制御だけ
              </h3>
              <p className="reveal">
                広告費の改善方法を探す中で、高額な広告運用ツールを導入しました。
                しかし本当に欲しかったのは<strong>時間帯ON/OFF</strong>の機能だけ。
                最適化部分は期待外れで、ON/OFFのためだけに毎月数万円を払うことになり、数か月後に解約しました。
              </p>
            </div>
          </div>
          <div className="sl-container sl-zigzag" style={{ marginTop: 16 }}>
            <div className="sl-zigzag__media reveal">
              <ManualImage
                src="/images/soft.jpg"
                alt="ツール開発"
                className="sl-image--card"
                setLightboxSrc={setLightboxSrc}
              />
            </div>
            <div className="sl-zigzag__copy">
              <h3 className="sl-subheading reveal">
                自分で開発したのがAdSche
              </h3>
              <p className="reveal">
                売れない時間帯の広告費を止め、売れる時間帯に予算を残す。
                それだけを自動でやってくれるツールが欲しくて、自分で<strong>AdSche</strong>を作りました。
                一度設定すれば、毎日の確認作業も旅行中の心配もなくなります。
              </p>
            </div>
          </div>
        </section>

        {/* Data Insight (compressed) */}
        <section className="sl-section" aria-labelledby="insight-title">
          <div className="sl-container">
            <h2 id="insight-title" className="sl-heading reveal">
              データが示す、時間帯最適化の必然
            </h2>
            <p className="sl-lead reveal">
              購入は<strong>曜日×時間帯</strong>で大きく変わります。
              売れない時間帯に広告費を使い続けるほど、ACOSは悪化しやすくなります。
              <br />
              ※取り扱う商品によりパターンは異なります。
            </p>
            <div className="sl-zigzag sl-zigzag--reverse" style={{ marginTop: 12 }}>
              <div className="sl-zigzag__copy">
                <p className="reveal">
                  平日昼はインプレッションは増えても購入に繋がりにくく、
                  <strong>広告費の無駄が発生しやすい</strong>時間帯です。
                  一方、夜や週末は比較検討から購入へ進みやすく、
                  同じ予算でも成果の質が変わります。
                </p>
                <p className="reveal">
                  手動で毎時間切り替えるのは現実的ではありません。
                  スケジュールを一度組めば、
                  <strong>売れない時間の広告費消化を自動で防げます</strong>。
                </p>
              </div>
              <div className="sl-zigzag__media reveal">
                <ManualImage
                  src="/images/analysis.jpg"
                  alt="時間帯別のCTR/CVRの傾向（概念図）"
                  className="sl-image--card"
                  setLightboxSrc={setLightboxSrc}
                />
              </div>
            </div>
            <div className="sl-grid" style={{ marginTop: 16 }}>
              <article className="sl-card reveal">
                <h4 className="sl-card__title">広告費の無駄</h4>
                <p className="sl-card__text">
                  売れない時間帯への露出は、クリックだけ増えて<strong>利益を圧迫</strong>します。
                </p>
              </article>
              <article className="sl-card reveal">
                <h4 className="sl-card__title">予算の偏り</h4>
                <p className="sl-card__text">
                  朝には予算が尽き、<strong>売れる時間帯に予算が残らない</strong>状態になりがちです。
                </p>
              </article>
              <article className="sl-card reveal">
                <h4 className="sl-card__title">自動化の効果</h4>
                <p className="sl-card__text">
                  スケジュール設定だけで、<strong>同じ売上でも広告費を抑える</strong>運用が可能です。
                </p>
              </article>
              <article className="sl-card reveal">
                <h4 className="sl-card__title">※注意※</h4>
                <p className="sl-card__text">
                  数値はカテゴリや商材で変動。<strong>自社の傾向</strong>に合わせた設定が重要です。
                </p>
              </article>
            </div>
          </div>
        </section>

        {/* Features (result-focused) */}
        <section
          className="sl-section sl-section--muted"
          aria-labelledby="features-title"
        >
          <div className="sl-container">
            <h2 id="features-title" className="sl-heading reveal">
              AdScheで得られる成果
            </h2>
            <p className="sl-lead reveal">
              機能の説明ではなく、<strong>あなたのビジネスに起きる変化</strong>をお伝えします。
            </p>
            <div className="sl-grid">
              <article className="sl-card reveal">
                <h3 className="sl-card__title">広告費の無駄を削減</h3>
                <p className="sl-card__text">
                  売れない時間帯への広告費消化を防ぎます。
                  深夜・早朝のクリックだけ増える浪費をカットし、利益改善につながります。
                </p>
              </article>
              <article className="sl-card reveal">
                <h3 className="sl-card__title">売れる時間に予算を集中</h3>
                <p className="sl-card__text">
                  売れる時間帯に広告予算を集中できます。
                  朝に予算が尽きる問題を解消し、成果の出やすい時間に投資を残せます。
                </p>
              </article>
              <article className="sl-card reveal">
                <h3 className="sl-card__title">毎日の確認作業を削減</h3>
                <p className="sl-card__text">
                  一度設定すれば自動で運用が回ります。
                  毎日キャンペーンマネージャーを開く必要がなくなり、作業時間を大幅に削減できます。
                </p>
              </article>
              <article className="sl-card reveal">
                <h3 className="sl-card__title">旅行中も安心</h3>
                <p className="sl-card__text">
                  スマホで毎日操作する必要はありません。
                  外出や旅行中も広告設定を気にせず、本業に集中できます。
                </p>
              </article>
            </div>
            <div className="sl-center reveal" style={{ marginTop: 24 }}>
              {CTA}
            </div>
          </div>
        </section>

        {/* How it Works */}
        <section className="sl-section" aria-labelledby="how-title">
          <div className="sl-container">
            <h2 id="how-title" className="sl-heading reveal">
              ご利用の流れ
            </h2>
            <p className="sl-lead reveal">
              セットアップは<strong>3ステップ</strong>。以後は放っておいても自動で回り続けます。
            </p>
            <ol className="sl-steps reveal">
              <li>
                <span className="sl-stepnum">1</span>
                <div>
                  <strong>「無料で試す」ボタンより開始</strong>
                  <p>
                    メール確認でStripeで申し込み。無料トライアル期間中でも
                    <strong>すべての機能が制限なし</strong>で利用可能。
                  </p>
                </div>
              </li>
              <li>
                <span className="sl-stepnum">2</span>
                <div>
                  <strong>ツールログイン後Amazonアカウント連携</strong>
                  <p>
                    正規のAmazon Ads
                    APIを使用。利用停止後に自動で取り消されます。権限は必要最小限だけ要求。
                  </p>
                </div>
              </li>
              <li>
                <span className="sl-stepnum">3</span>
                <div>
                  <strong>スケジュール登録</strong>
                  <p>
                    曜日と時間、ON/OFF、予算をカレンダー感覚で設定。変更がなければ一度の設定で以降ツールを見なくてもOK。
                  </p>
                </div>
              </li>
            </ol>

            <div className="sl-zigzag" style={{ marginTop: 16 }}>
              <div className="sl-zigzag__media reveal">
                <ManualImage
                  src="images/schedule-ui.jpg"
                  alt="スケジュール設定UI（例）"
                  className="sl-image--card"
                  setLightboxSrc={setLightboxSrc}
                />
              </div>
              <div className="sl-zigzag__copy">
                <h3 className="sl-subheading reveal">よく使うパターン</h3>
                <ul className="sl-list reveal">
                  <li>平日23:00–06:59はOFF、20:00–23:00は予算+30%</li>
                  <li>金・土は終日ON、日曜夜は控えめに</li>
                  <li>プライムデー当日は時間帯ごとに上限を引き上げ</li>
                </ul>
                <p className="reveal">
                  スケジュール設定は保存と同時に反映。以後は<strong>完全自動</strong>
                  でスケジュールが開始されます。
                </p>
              </div>
            </div>
            <div className="sl-video-wrap reveal" style={{ marginTop: 24 }}>
              <div className="sl-video">
                <iframe
                  src="https://www.youtube.com/embed/5r0GrK1w4RY?si=UVoWeE6yaVvPYw7a"
                  title="AdSche 操作デモ動画"
                  allowFullScreen
                ></iframe>
              </div>
            </div>
          </div>
        </section>

        {/* Pricing */}
        <section
          className="sl-section sl-section--muted"
          aria-labelledby="pricing-title"
        >
          <div className="sl-container sl-pricing reveal">
            <div className="sl-pricing__badge">FREE TRIAL</div>
            <h2 id="pricing-title" className="sl-heading">
              料金
            </h2>
            <p className="sl-pricing__price">月額3,980円（税込）</p>
            <p className="sl-pricing__daily">1日あたり約133円</p>
            <p className="sl-lead">
              広告費を1日133円以上削減できれば元が取れる計算です。
            </p>
            <div className="sl-pricing__roi">
              <p className="sl-pricing__roi-title">導入事例での費用対効果（ケース1）</p>
              <div className="sl-pricing__roi-row">
                <span>毎月の広告費削減</span>
                <span>148,000円</span>
              </div>
              <div className="sl-pricing__roi-row">
                <span>AdSche料金</span>
                <span>3,980円</span>
              </div>
              <div className="sl-pricing__roi-row sl-pricing__roi-row--highlight">
                <span>差額（毎月の利益改善）</span>
                <span>144,020円</span>
              </div>
            </div>
            <p className="sl-lead">
              まずは<strong>30日間の無料トライアル</strong>
              で全機能をお試しください。
              <br />
              期間中は料金は発生しません。気に入ったらそのまま継続できます。
            </p>
            {CTA}
            <p className="sl-pricing__note">
              ※ いつでもキャンセル可能
            </p>
          </div>
        </section>

        {/* FAQ */}
        <section className="sl-section" aria-labelledby="faq-title">
          <div className="sl-container">
            <h2 id="faq-title" className="sl-heading reveal">
              よくある質問
            </h2>
            <CollapsibleSection
              title="本当に30日間無料ですか？"
              defaultOpen={false}
            >
              <p>
                はい。<strong>期間中は課金されません</strong>
                。継続をご希望の場合のみ、そのまま本契約に移行します。
              </p>
            </CollapsibleSection>
            <CollapsibleSection
              title="無料トライアル期間後の料金はいくらですか？"
              defaultOpen={false}
            >
              <p>
                3,980円（税込み）となります。30日毎に課金が行われます。
                <br />
                1日あたり約133円。広告費を1日133円以上削減できれば元が取れる計算です。
                <br />
                ※将来的に料金は値上がりする可能性がありますが、値上がり後も既存ユーザーは既存料金のままご利用頂けます。
              </p>
            </CollapsibleSection>
            <CollapsibleSection
              title="決済媒体は何になりますか？"
              defaultOpen={false}
            >
              <p>
                現時点ではStripeのみとなります。
              </p>
            </CollapsibleSection>
            <CollapsibleSection
              title="途中でやめたらどうなりますか？"
              defaultOpen={false}
            >
              <p>
                次回請求前に自動停止します。
                <strong>トライアル中の料金は発生しません。</strong>
              </p>
            </CollapsibleSection>
            <CollapsibleSection title="連携は安全ですか？" defaultOpen={false}>
              <p>
                正規のAmazon Ads APIのみを利用し、必要最小限の権限で接続しますので安全です。
              </p>
            </CollapsibleSection>
            <CollapsibleSection
              title="設定は難しくありませんか？"
              defaultOpen={false}
            >
              <p>
                PCでの基本操作が出来る方であれば、問題なく操作可能です。
              </p>
            </CollapsibleSection>
            <CollapsibleSection
              title="スケジュールは何件まで登録可能でしょうか？"
              defaultOpen={false}
            >
              <p>
                決まった件数はありませんが、<strong>同一時間で100件以上</strong>登録される場合は、制限対象となる場合があります。
                <br />
                同一時間で100件以上登録したい場合は、前もってご相談ください。
                <br />
              </p>
            </CollapsibleSection>
            <CollapsibleSection
              title="スケジュールの実行はタイムラグがありますか？"
              defaultOpen={false}
            >
              <p>
                設定スケジュール時刻にシステムが動き出しますので、設定時刻ピッタリには反映されません。
                <br />
                同一時刻での利用者全体の総スケジュール登録数にもよりますが、早ければ数秒、遅くとも数分で反映されるような設計となっております。
              </p>
            </CollapsibleSection>
            <CollapsibleSection
              title="Amazonのセラーアカウントを複数所持していますが、1つのツールで複数管理できますか？"
              defaultOpen={false}
            >
              <p>
                複数管理はできません。AdScheアカウント1つにつきAmazonセラーアカウント1つの利用となります。
                <br />
                もし複数のセラーアカウントを管理したい場合は、AdScheアカウントが複数必要となります。
                <br />
                ※サブスクリプションはアカウントごとに発生します。
              </p>
            </CollapsibleSection>
          </div>
        </section>

        {/* Final CTA */}
        <section
          className="sl-section sl-section--final"
          aria-labelledby="cta-final-title"
        >
          <div className="sl-container sl-final">
            <h2 id="cta-final-title" className="sl-heading reveal">
              まずは30日間無料でお試しください
            </h2>
            <p className="sl-final__lead reveal">
              広告費の無駄を減らし、
              <br />
              売れる時間帯に予算を残す。
              <br />
              その効果をまずはご自身のアカウントで確認してください。
              <br />
              30日間は完全無料です。
            </p>
            <div className="sl-final__cta">{CTA}</div>
            <p className="sl-final__note">
              ※ トライアルはすぐに開始できます。「無料で試す」ボタンよりお進みください。
            </p>
          </div>
        </section>

        <footer className="sl-footer">
          <div className="sl-container">
            <a
              href="/tokushoho"
              className="sl-footer__link"
              target="_blank"
              rel="noopener noreferrer"
            >
              特定商取引法に基づく表記
            </a>
          </div>
        </footer>
      </main>
    </>
  );
}
